import React, { useEffect, useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import moment from 'moment';
import { useVendorBankAccount } from '../store/slice/vendorBankAccountSlice';
import AdvancedFilter from '../components/AdvancedFilter';
import { ActionLink } from '../components/ActionControls';

const PAGE_SIZE = 20;

function maskAccountNumber(accountNumber) {
  if (!accountNumber) return '—';
  const str = String(accountNumber);
  if (str.length <= 4) return str;
  return `****${str.slice(-4)}`;
}

function formatAccountType(value) {
  if (!value) return '—';
  return String(value).charAt(0).toUpperCase() + String(value).slice(1);
}

function isVendorApproved(value) {
  if (value === true || value === 'true' || value === 1 || value === '1') return true;
  if (value === false || value === 'false' || value === 0 || value === '0') return false;
  return null;
}

function approvalStatusClass(approved) {
  if (approved === true) return 'status-pill-success';
  if (approved === false) return 'status-pill-warning';
  return 'status-pill-muted';
}

function approvalStatusLabel(approved) {
  if (approved === true) return 'Approved';
  if (approved === false) return 'Pending';
  return 'Unknown';
}

function VendorBankAccountsPage() {
  const { vendorBankAccounts, pagination, loading, error, fetchVendorBankAccounts } = useVendorBankAccount();

  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    search: '',
    approval: 'all',
  });

  const apiFilters = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
    }),
    [page]
  );

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  useEffect(() => {
    fetchVendorBankAccounts(apiFilters);
  }, [apiFilters, fetchVendorBankAccounts]);

  const handleFilterChange = (key, value) => {
    setFilters((p) => ({ ...p, [key]: value }));
  };

  const filterConfig = [
    { key: 'search', type: 'search', value: filters.search },
    {
      key: 'approval',
      type: 'select',
      label: 'Vendor Approval',
      value: filters.approval,
      defaultValue: 'all',
      options: [
        { value: 'approved', label: 'Approved' },
        { value: 'pending', label: 'Pending' },
      ],
    },
  ];

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.search) count++;
    if (filters.approval && filters.approval !== 'all') count++;
    return count;
  }, [filters]);

  const filteredAccounts = useMemo(() => {
    let result = [...vendorBankAccounts];

    if (filters.search.trim()) {
      const term = filters.search.toLowerCase();
      result = result.filter(
        (a) =>
          (a.vendor_name || '').toLowerCase().includes(term) ||
          (a.account_holder_name || '').toLowerCase().includes(term) ||
          (a.bank_name || '').toLowerCase().includes(term) ||
          (a.account_number || '').includes(term) ||
          (a.ifsc_code || '').toLowerCase().includes(term) ||
          (a.vendor_email || '').toLowerCase().includes(term) ||
          (a.vendor_phone || '').includes(term)
      );
    }

    if (filters.approval === 'approved') {
      result = result.filter((a) => isVendorApproved(a.vendor_is_approved) === true);
    } else if (filters.approval === 'pending') {
      result = result.filter((a) => isVendorApproved(a.vendor_is_approved) === false);
    }

    return result;
  }, [vendorBankAccounts, filters.search, filters.approval]);

  const approvedOnPage = useMemo(
    () => filteredAccounts.filter((a) => isVendorApproved(a.vendor_is_approved) === true).length,
    [filteredAccounts]
  );

  const currentPage = pagination.page || page;
  const totalPages = pagination.totalPages || 0;
  const totalAccounts = pagination.total || 0;
  const canGoPrev = currentPage > 1;
  const canGoNext = totalPages > 0 && currentPage < totalPages;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-3xl text-slate-800 tracking-tight">Vendor Bank Accounts</h1>
          <p className="mt-1 text-slate-500 text-sm">View bank account details registered by vendors</p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="card-premium p-5">
          <p className="text-sm font-medium text-slate-500">Total Accounts</p>
          <p className="mt-1 text-2xl font-bold text-slate-800">{totalAccounts}</p>
        </div>
        <div className="card-premium p-5">
          <p className="text-sm font-medium text-slate-500">On This Page</p>
          <p className="mt-1 text-2xl font-bold text-amber-600">{filteredAccounts.length}</p>
        </div>
        <div className="card-premium p-5">
          <p className="text-sm font-medium text-slate-500">Approved Vendors (page)</p>
          <p className="mt-1 text-2xl font-bold text-emerald-600">{approvedOnPage}</p>
        </div>
      </div>

      <AdvancedFilter
        searchPlaceholder="Search vendor, bank, account, IFSC, email, or phone..."
        filters={filterConfig}
        onFilterChange={handleFilterChange}
        activeCount={activeFilterCount}
      />

      <div className="card-premium overflow-hidden">
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/80">
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Vendor</th>
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Account Holder</th>
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Bank</th>
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Account Number</th>
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">IFSC</th>
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Type</th>
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Vendor Status</th>
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Added</th>
                <th className="text-right py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAccounts.map((account) => {
                const approved = isVendorApproved(account.vendor_is_approved);

                return (
                  <tr key={account.id} className="border-b border-slate-50 hover:bg-slate-50/60 transition-colors">
                    <td className="py-4 px-6">
                      <p className="font-medium text-slate-800">{account.vendor_name || '—'}</p>
                      <p className="text-sm text-slate-500">{account.vendor_email || '—'}</p>
                      {account.vendor_phone && (
                        <p className="text-sm text-slate-400">{account.vendor_phone}</p>
                      )}
                    </td>
                    <td className="py-4 px-6 text-slate-600">{account.account_holder_name || '—'}</td>
                    <td className="py-4 px-6 text-slate-600">
                      <p>{account.bank_name || '—'}</p>
                      {account.branch_name && (
                        <p className="text-sm text-slate-400">{account.branch_name}</p>
                      )}
                    </td>
                    <td className="py-4 px-6 text-slate-600 font-mono text-sm">
                      {maskAccountNumber(account.account_number)}
                    </td>
                    <td className="py-4 px-6 text-slate-600 font-mono text-sm">{account.ifsc_code || '—'}</td>
                    <td className="py-4 px-6 text-slate-600 capitalize">{formatAccountType(account.account_type)}</td>
                    <td className="py-4 px-6">
                      <span className={`status-pill ${approvalStatusClass(approved)}`}>
                        {approvalStatusLabel(approved)}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-slate-600 text-sm">
                      {account.created_at ? moment(account.created_at).format('DD/MM/YYYY') : '—'}
                    </td>
                    <td className="py-4 px-6 text-right">
                      <ActionLink to={`/vendor-bank-accounts/user/${account.user_id}`} variant="neutral">
                        View
                      </ActionLink>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {loading && vendorBankAccounts.length === 0 && (
          <div className="py-16 text-center text-slate-500">Loading vendor bank accounts...</div>
        )}
        {!loading && filteredAccounts.length === 0 && (
          <div className="py-16 text-center text-slate-500">No vendor bank accounts found.</div>
        )}

        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-slate-100 px-6 py-4">
            <p className="text-sm text-slate-500">
              Page {currentPage} of {totalPages} · {totalAccounts} accounts total
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={!canGoPrev || loading}
                className="px-4 py-2 rounded-premium border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setPage((prev) => prev + 1)}
                disabled={!canGoNext || loading}
                className="px-4 py-2 rounded-premium border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default VendorBankAccountsPage;
